import { Sparkle, Ear, Eye, HandTap, Confetti, Brain, ShieldCheck } from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../../library/LibraryPrimitives";
import { Flow } from "../diagrams";

const LOOP = [
  { icon: Ear, label: "Listen", desc: "Narration reads questions, hints and stories aloud.", tone: "blue" },
  { icon: Eye, label: "Observe", desc: "Bright scenes and characters show what to do.", tone: "teal" },
  { icon: HandTap, label: "Interact", desc: "Tap, drag and choose — learning by doing.", tone: "orange" },
  { icon: Confetti, label: "Celebrate", desc: "Effort is rewarded; progress is remembered.", tone: "yellow" },
];

const LAYERS = [
  { label: "Areas", tone: "bg-primary/12 text-primary-strong", items: "Home · Learn · Homework · Rewards · Progress · Profile", note: "Where children go" },
  { label: "Learning modes", tone: "bg-blue/12 text-blue-ink", items: "Quick Practice · Story Adventures · Puzzles · Reading Journeys · Daily Challenges · Homework", note: "How they learn" },
  { label: "Multi-sensory delivery", tone: "bg-teal/15 text-teal-ink", items: "Visual · Audio · Interactive · Story · Text", note: "How it reaches them" },
  { label: "Memory & feedback", tone: "bg-secondary/25 text-secondary-ink", items: "Progress over time · Mastery growth · Encouragement that never punishes", note: "What keeps them growing" },
];

export function OverviewSection() {
  return (
    <SectionShell
      id="overview"
      eyebrow="Overview"
      eyebrowIcon={Sparkle}
      title="A calm ecosystem, not a pile of screens"
      description="Bloom is designed as layers that work together — places to go, ways to learn, senses to reach, and a memory that celebrates growth. Every layer stays warm, safe and never overwhelming."
    >
      <div className="space-y-8">
        <Showcase title="The core learning loop" description="Every activity, for every age, follows the same gentle rhythm">
          <Flow steps={LOOP} />
          <p className="mt-4 text-sm font-medium text-muted-foreground">
            For Year 1 this loop is mostly <strong className="text-foreground">Listen &amp; Observe</strong>; by Year 6 it leans on <strong className="text-foreground">Read &amp; Interact</strong> — but the shape never changes.
          </p>
        </Showcase>

        <Showcase title="How the ecosystem stacks" tone="muted">
          <div className="space-y-3">
            {LAYERS.map((l, i) => (
              <div key={l.label} className="flex flex-col gap-2 rounded-2xl bg-card p-4 shadow-soft sm:flex-row sm:items-center sm:gap-4">
                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl text-sm font-black ${l.tone}`}>{i + 1}</span>
                <div className="sm:w-40 sm:flex-shrink-0">
                  <p className="font-display font-extrabold">{l.label}</p>
                  <p className="text-xs font-bold text-muted-foreground">{l.note}</p>
                </div>
                <p className="font-medium text-muted-foreground">{l.items}</p>
              </div>
            ))}
          </div>
        </Showcase>

        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Brain, t: "Learn by doing", d: "Children act first, read later — comprehension is never a gate." },
            { icon: ShieldCheck, t: "Safe & calm", d: "No pressure, no leaderboards, no dead ends. Always a kind next step." },
            { icon: Confetti, t: "Grows with them", d: "The same system matures in tone and density from Year 1 to Year 6." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-3xl border border-border bg-card p-5 shadow-soft">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong"><Icon size={22} weight="fill" /></span>
              <p className="mt-3 font-display text-lg font-extrabold">{t}</p>
              <p className="mt-1 text-sm font-medium leading-relaxed text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

export default OverviewSection;
